import React, { useEffect, useRef, useState } from 'react'
import { Stack } from 'expo-router'
import { Pressable, ScrollView, Text, View, useWindowDimensions } from 'react-native'
import { PLAYER_COLORS, clampPlayerSlot } from '@warcarp/ui'
import { useTheme } from '../../../hooks/useTheme'
import { ATTACK_LOG_CONCEPTS, type AttackLogConcept } from '../../../preview/attackLogConcepts'
import { ConceptMiniMap } from '../../../preview/ConceptMiniMap'
import { AttackLogCard } from '../../../components/turn/AttackLogCard'
import { TurnControlCard } from '../../../components/turn/TurnControlCard'
import { TurnActionCounter } from '../../../components/turn/TurnActionCounter'
import { FortifyButtonLabel } from '../../../components/turn/FortifyButtonLabel'
import { FormattedLogText } from '../../../components/game/FormattedLogText'
import { MapControlsBar } from '../../../components/map/MapControlsBar'
import { readablePlayerTextColor } from '../../../lib/playerTextColor'
import { TURN_PANEL_COLORS } from '../../../lib/turnPanelAppearance'

function Sample({ concept, slots, map = false }: { concept: AttackLogConcept; slots: [number, number]; map?: boolean }) {
  const { theme } = useTheme()
  const [expanded, setExpanded] = useState(false)
  const [conquered, setConquered] = useState(true)
  const [rolls, setRolls] = useState(1)
  const dark = theme === 'dark'
  const ink = dark ? '#e6e1c4' : '#18170f'
  return <View style={{ backgroundColor: TURN_PANEL_COLORS[theme].background }}>
    {map ? <><ConceptMiniMap dark={dark} height={180} /><MapControlsBar onReset={() => undefined} onZoomIn={() => undefined} onZoomOut={() => undefined} canZoomIn canZoomOut compact mode="game" onModeChange={() => undefined} /></> : null}
    <View style={{ padding: 10, gap: 10 }}>
      <TurnControlCard style={{ flexDirection: 'row', alignItems: 'center' }}>
        <TurnActionCounter action="attacks" used={rolls} limit={null} compact />
        <View style={{ marginLeft: 'auto', backgroundColor: dark ? '#8aa14a' : '#46532b', borderRadius: 6, padding: 8 }}><FortifyButtonLabel fontSize={10} /></View>
      </TurnControlCard>
      <AttackLogCard appearance={concept.appearance[theme]} rollCount={rolls} conquered={conquered}
        summary={`yakuza (${rolls} lost) vs Tamerlane (${rolls} lost)`}
        highlights={[{ text: 'yakuza', color: PLAYER_COLORS[clampPlayerSlot(slots[0])].hex }, { text: 'Tamerlane', color: PLAYER_COLORS[clampPlayerSlot(slots[1])].hex }]}
        expanded={expanded} onToggle={() => setExpanded(v => !v)}>
        {Array.from({ length: rolls }, (_, index) => <FormattedLogText key={index} text={`${index + 1}. [6][3][1] vs [5][3] (1 lost, 1 lost)`} fontSize={13} diceSize={24} color={ink} style={{ color: ink, fontSize: 13 }} className="font-typed" />)}
      </AttackLogCard>
      {map ? <View style={{ flexDirection: 'row', gap: 16, paddingTop: 4 }}>
        <Pressable accessibilityRole="button" onPress={() => { setRolls(v => v === 4 ? 1 : v + 1); setExpanded(true) }}><Text className="font-typed" style={{ color: ink }}>Add sample roll</Text></Pressable>
        <Pressable accessibilityRole="button" onPress={() => setConquered(v => !v)}><Text className="font-typed" style={{ color: ink }}>{conquered ? 'Hide conquered' : 'Show conquered'}</Text></Pressable>
      </View> : null}
    </View>
  </View>
}

export default function AttackLogConceptGallery() {
  const { width } = useWindowDimensions()
  const { theme, setTheme } = useTheme()
  const original = useRef(theme)
  useEffect(() => {
    const restore = original.current
    setTheme('dark')
    return () => setTheme(restore)
  }, [setTheme])
  const scroll = useRef<ScrollView>(null)
  const [selected, setSelected] = useState(2)
  const [slots, setSlots] = useState<[number, number]>([5, 2])
  const dark = theme === 'dark'
  const ink = dark ? '#e6e1c4' : '#18170f'
  const soft = dark ? '#b4b79e' : '#66694f'
  const border = dark ? '#68774a' : '#a0a77b'
  const canvas = dark ? '#161d10' : '#eee5cb'
  const contentWidth = Math.min(width - 32, 1320)
  const columns = width >= 1100 ? 3 : width >= 720 ? 2 : 1
  const cardWidth = (contentWidth - (columns - 1) * 16) / columns
  const button = { borderWidth: 1, borderColor: border, borderRadius: 7, paddingHorizontal: 12, paddingVertical: 9 } as const
  return <ScrollView ref={scroll} style={{ flex: 1, backgroundColor: canvas }} contentContainerStyle={{ padding: 16, gap: 20, alignItems: 'center' }}>
    <Stack.Screen options={{ title: 'Attack summary · 12 concepts', headerStyle: { backgroundColor: canvas }, headerTintColor: ink }} />
    <View style={{ width: contentWidth, gap: 12 }}>
      <Text className="font-stencil" style={{ fontSize: width < 600 ? 24 : 30, color: ink }}>A quieter combat report</Text>
      <Text className="font-typed" style={{ fontSize: 16, color: soft }}>Twelve surfaces beside the existing controls. Every option uses the real attack-log component, readable player names and a smaller heading. Tap a report to expand its rolls.</Text>
      <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 10 }}>
        <Pressable style={button} accessibilityRole="button" onPress={() => setTheme(dark ? 'light' : 'dark')}><Text className="font-stencil" style={{ color: ink, fontSize: 11 }}>{dark ? 'Light mode' : 'Dark mode'}</Text></Pressable>
        <Pressable style={button} accessibilityRole="button" onPress={() => setSlots(([a, b]) => [a % 6 + 1, b % 6 + 1])}><Text className="font-stencil" style={{ color: ink, fontSize: 11 }}>Next player colors</Text></Pressable>
      </View>
      <View style={{ flexDirection: 'row', gap: 14, flexWrap: 'wrap' }}>
        {dark ? Object.values(PLAYER_COLORS).map(color => <Text key={color.name} className="font-typed" style={{ fontSize: 13, color: readablePlayerTextColor(color.hex, '#303923') }}>{color.name}</Text>) : null}
      </View>
      <View style={{ borderWidth: 1, borderColor: border, borderRadius: 12, overflow: 'hidden', maxWidth: 650 }}>
        <View style={{ padding: 12 }}><Text className="font-stencil" style={{ fontSize: 12, color: ink }}>{String(selected + 1).padStart(2, '0')} · {ATTACK_LOG_CONCEPTS[selected]!.name} · in context</Text></View>
        <Sample key={ATTACK_LOG_CONCEPTS[selected]!.id} concept={ATTACK_LOG_CONCEPTS[selected]!} slots={slots} map />
      </View>
    </View>
    <View style={{ width: contentWidth, flexDirection: 'row', flexWrap: 'wrap', gap: 16 }}>
      {ATTACK_LOG_CONCEPTS.map((concept, i) => <View key={concept.id} testID={`attack-concept-${concept.id}`} style={{ width: cardWidth, borderWidth: selected === i ? 2 : 1, borderColor: selected === i ? (dark ? '#b3cc66' : '#46532b') : border, borderRadius: 12, overflow: 'hidden' }}>
        <View style={{ padding: 14, gap: 8 }}>
          <Text className="font-stencil" style={{ fontSize: 13, color: ink }}>{String(i + 1).padStart(2, '0')} · {concept.name}</Text>
          <Text className="font-typed" style={{ fontSize: 14, color: soft, minHeight: 40 }}>{concept.description}</Text>
        </View>
        <Sample concept={concept} slots={slots} />
        <Pressable style={{ padding: 14 }} accessibilityRole="button" accessibilityLabel={`View ${concept.name} in context`} onPress={() => { setSelected(i); scroll.current?.scrollTo({ y: 0, animated: false }) }}><Text className="font-stencil" style={{ fontSize: 10, color: ink }}>View with map ↑</Text></Pressable>
      </View>)}
    </View>
  </ScrollView>
}
